import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { trackPageView } from '../utils/analytics'

const LAST_UPDATED = 'September 27, 2026'

// Single source of truth for the privacy policy - the mobile app links out to this
// page rather than duplicating the copy in-app.
export const PrivacyPolicyPage = () => {
  useEffect(() => {
    try {
      trackPageView({ page: '/privacy' })
    } catch (error) {
      console.error('Failed to track privacy policy page view:', error)
    }
  }, [])

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 text-left">
      <h1 className="text-2xl font-bold mb-2">Zwizle Privacy Policy</h1>
      <p className="text-sm text-gray-500 mb-8">Last updated: {LAST_UPDATED}</p>

      <div className="space-y-8 leading-relaxed">
        <section>
          <p>
            Zwizle ("Zwizle", "we", "us") provides Shona-language word games —
            Zword and Anagram — through our website and mobile app
            (collectively, the "Service"). This policy explains what information
            we collect when you use the Service, why we collect it, and the
            choices you have. You can play Zwizle as a guest without creating an
            account; some information below only applies if you choose to
            register.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">Information we collect</h2>

          <h3 className="text-lg font-medium mt-4 mb-1">
            Account information (optional)
          </h3>
          <p>
            In the mobile app you can sign in with Google, with Apple (on
            iPhone), or with a one-time code we email to you. Zwizle does not
            use passwords for these sign-ins. We collect your email address and
            a unique account identifier, and we use them to let you log in and
            manage your account.
          </p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>
              <strong>Google:</strong> Google shares the basic profile details
              you allow (such as your email address, name, and profile
              picture) with our authentication service, Firebase Authentication.
            </li>
            <li>
              <strong>Apple:</strong> we ask Apple only for your email address.
              If you choose "Hide My Email", we receive an Apple-generated
              relay address instead of your real one.
            </li>
            <li>
              <strong>Emailed code:</strong> when you ask for a code, we send a
              6-digit code to the email address you entered. We store only a
              secret-keyed hash of the code, never the code itself. It expires
              after 10 minutes, works once, and is deleted when used. Requests
              and failed attempts are rate limited, and our security logs record
              the email address only as a one-way hash.
            </li>
          </ul>
          <p className="mt-2">
            <strong>Your public name.</strong> When you sign in on the mobile
            app, we create a player profile with a display name and a short
            tag, shown to other players on the leaderboard as{' '}
            <em>Name #TAG</em>. The name starts as the guest name you already
            used on that phone (or "Player") and you can change it at any time.
            It is public, so please do not use your real name if you would
            rather not. We check names against a filter for offensive words.
            Your email address is never shown to other players.
          </p>
          <p className="mt-2">
            <strong>Linking your phone.</strong> Signing in links the anonymous
            device identifier below to your account, so your name appears on
            games you played on that phone before you signed in. A phone can be
            linked to one account. Deleting your account removes the link, and
            those games go back to appearing under the plain guest name.
          </p>
          <p className="mt-2">
            The Zwizle website also has a separate login for word setters,
            which uses an email address and a password (stored only as a salted
            hash — we never store or can see your plaintext password).
          </p>

          <h3 className="text-lg font-medium mt-4 mb-1">
            Guest play and device identifiers
          </h3>
          <p>
            If you play without an account, we assign your browser or app
            installation a random, anonymous device identifier so we can track
            daily puzzle progress and prevent abuse (e.g. one submission per
            puzzle per device). If you appear on a leaderboard as a guest, you
            choose the display name shown there — it does not need to be your
            real name.
          </p>

          <h3 className="text-lg font-medium mt-4 mb-1">Gameplay data</h3>
          <p>
            We store the puzzles you've played, your guesses, completion times,
            streaks, and guess-distribution statistics, so we can show you your
            stats and, for registered users, sync them across devices. Some of
            this is also kept locally on your device (for example, in your
            browser's local storage) so the game works offline and loads
            instantly.
          </p>

          <h3 className="text-lg font-medium mt-4 mb-1">
            Technical and usage data
          </h3>
          <p>
            We use Firebase Analytics and, on the web app, Hotjar to understand
            how people use Zwizle — for example, which puzzles are played,
            session length, device type, and general navigation patterns. This
            data is tied to a device or anonymous analytics identifier, not your
            name, unless you're logged in and we associate your account for
            support purposes.
          </p>
          <p className="mt-2">
            We also use Sentry for crash and error reporting so we can find and
            fix bugs. On the web app, Sentry may capture a limited,
            privacy-filtered session replay (a recording of on-screen
            interactions) for a sample of sessions or when an error occurs, to
            help us diagnose issues; this is not enabled on the mobile app.
            Sentry reports may include technical details like device type,
            browser/OS version, and the page or screen you were on when an error
            happened.
          </p>
          <p className="mt-2">
            Our backend temporarily processes your IP address to apply rate
            limiting and prevent abuse of the puzzle-submission system. We store
            a one-way cryptographic hash of your IP address for this purpose,
            not the IP address itself, and it isn't used to identify you
            personally.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">
            How we use this information
          </h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              To run the core game: serve daily puzzles, validate guesses, and
              track your progress and streaks.
            </li>
            <li>
              To let you log in and keep your stats in sync across devices, if
              you have an account.
            </li>
            <li>
              To send you the one-time sign-in codes you ask for. We do not use
              your email address for marketing.
            </li>
            <li>
              To show leaderboards and the guest name you choose to display.
            </li>
            <li>
              To understand aggregate usage and improve the games (Firebase
              Analytics, Hotjar).
            </li>
            <li>To find and fix bugs and crashes (Sentry).</li>
            <li>
              To prevent abuse of the puzzle system, such as automated or
              repeated submissions (hashed IP, device identifiers).
            </li>
          </ul>
          <p className="mt-2">
            We do not sell your personal information, and we do not use it for
            third-party advertising.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">Third-party services</h2>
          <p>
            We share limited data with the following service providers, who
            process it on our behalf:
          </p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>
              <strong>Firebase (Google)</strong> — hosting, analytics, and our
              database. See Google's{' '}
              <a
                className="underline"
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noreferrer"
              >
                privacy policy
              </a>
              .
            </li>
            <li>
              <strong>Google Sign-In and Apple Sign in with Apple</strong> —
              if you choose to sign in with them. Their own privacy policies
              apply to what they collect:{' '}
              <a
                className="underline"
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noreferrer"
              >
                Google
              </a>
              ,{' '}
              <a
                className="underline"
                href="https://www.apple.com/legal/privacy/"
                target="_blank"
                rel="noreferrer"
              >
                Apple
              </a>
              .
            </li>
            <li>
              <strong>Resend</strong> — delivers the sign-in code emails we
              send you. It receives your email address and the message. See
              Resend's{' '}
              <a
                className="underline"
                href="https://resend.com/legal/privacy-policy"
                target="_blank"
                rel="noreferrer"
              >
                privacy policy
              </a>
              .
            </li>
            <li>
              <strong>Hotjar</strong> — usage analytics and heatmaps on the web
              app. See Hotjar's{' '}
              <a
                className="underline"
                href="https://www.hotjar.com/legal/policies/privacy/"
                target="_blank"
                rel="noreferrer"
              >
                privacy policy
              </a>
              .
            </li>
            <li>
              <strong>Sentry</strong> — error and crash reporting. See Sentry's{' '}
              <a
                className="underline"
                href="https://sentry.io/privacy/"
                target="_blank"
                rel="noreferrer"
              >
                privacy policy
              </a>
              .
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">Data retention</h2>
          <p>
            We keep account and gameplay data for as long as your account is
            active, or as long as needed to provide the Service. Data tied to
            anonymous device identifiers is kept for as long as needed to
            support daily-puzzle limits and abuse prevention. Sign-in codes are
            deleted when used and expire on their own within minutes; sign-in
            rate-limit records expire within an hour; and our security logs,
            which hold only one-way hashes rather than your email address, are
            deleted after 30 days. You can delete your account at any time
            (see below).
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">
            Your choices and rights
          </h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              You can play as a guest without providing any personal information
              beyond an optional display name.
            </li>
            <li>
              You can request access to, correction of, or deletion of your
              account data by contacting us below.
            </li>
            <li>
              You can clear locally stored game data at any time by clearing
              your browser's site data or uninstalling the mobile app.
            </li>
          </ul>
        </section>

        <section id="account-deletion">
          <h2 className="text-xl font-semibold mb-2">
            Deleting your Zwizle account
          </h2>
          <p>
            You can delete your Zwizle account instantly in the mobile app
            (Profile, then Delete account) or on the web at{' '}
            <Link className="underline" to="/delete-account">
              zwizle.com/delete-account
            </Link>
            , where we confirm the account is yours with a code sent to its
            email address. If you cannot use either, email{' '}
            <a className="underline" href="mailto:privacy@zwizle.com?subject=Delete%20my%20Zwizle%20account">
              privacy@zwizle.com
            </a>{' '}
            from the email address associated with your account, with the
            subject "Delete my Zwizle account." We verify the request and
            process it within 30 days.
          </p>
          <p className="mt-2">Deleting your account removes:</p>
          <ul className="list-disc pl-6 space-y-1 mt-1">
            <li>
              Your sign-in account, the email address linked to it, any
              sign-in codes still pending for it, and your player profile
              (display name and tag) and its device links. (For website word-setter
              accounts, your name and password hash.)
            </li>
            <li>
              Your saved gameplay stats and history tied to your account
              (streaks, completed puzzles, guess distribution).
            </li>
            <li>Your leaderboard entries linked to your account.</li>
          </ul>
          <p className="mt-2">We retain, and do not delete on request:</p>
          <ul className="list-disc pl-6 space-y-1 mt-1">
            <li>
              Aggregated or anonymized analytics that no longer identify you
              individually.
            </li>
            <li>
              Hashed IP data already collected for rate-limiting/abuse
              prevention, which is not linked back to your account or
              personally identifiable, until it expires (sign-in rate-limit
              records within an hour) or ages out of our systems, and security
              logs, which are deleted after 30 days.
            </li>
            <li>
              Records we're required to keep to comply with legal
              obligations, resolve disputes, or enforce our agreements.
            </li>
          </ul>
          <p className="mt-2">
            Uninstalling the app or clearing your browser's site data removes
            locally stored game data on that device, but does not delete your
            account — use one of the options above for that.
          </p>
          <p className="mt-2">
            If you signed in with Apple, you can also stop Zwizle using your
            Apple ID in your Apple ID settings, under "Sign in with Apple".
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">Children's privacy</h2>
          <p>
            Zwizle is not directed at children under 13, and we do not knowingly
            collect personal information from children under 13. If you believe
            a child has provided us with personal information, contact us and we
            will delete it.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">Changes to this policy</h2>
          <p>
            We may update this policy from time to time. We'll update the "Last
            updated" date above when we do. Continued use of the Service after a
            change means you accept the updated policy.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">Contact us</h2>
          <p>
            Questions about this policy or your data? Email us at{' '}
            <a className="underline" href="mailto:zwizleapp@gmail.com">
              zwizleapp@gmail.com
            </a>
            .
          </p>
        </section>
      </div>
    </div>
  )
}
